/**
 * CheckInV2View
 *
 * Main container for the treatment check-in v2 operator UI.
 * Route: /matters/:matter_id/treatment/check-in-v2/:checkInId
 *
 * Two-tab layout:
 *   Tab 1 — "Taking Notes"   (always clickable)
 *   Tab 2 — "Review & Submit" (always clickable)
 *
 * "Generate Notes" triggers AI generation and switches to the Review tab which
 * shows an inline loading spinner until results arrive. Operators can freely
 * switch between tabs at any time.
 */
import { AlertCircle, Check, Copy, FileText, Loader2, Maximize2, MessageSquare, Minimize2, Phone, PhoneOff, RefreshCw, Voicemail, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useGetMatterQuery } from '@/services/api/mattersService';
import { useInitiateRCCallMutation } from '@/services/api/ringcentralService';
import {
  type CallOutcome,
  type FinalizeConflictResponse,
  type ObjectiveStatus,
  type TreatmentCheckInV2,
  type TreatmentCheckInV2Status,
  useFinalizeCheckInV2Mutation,
  useGenerateEnhancedNotesMutation,
  useGetCheckInTranscriptSegmentsQuery,
  useGetCheckInV2Query,
  useRecordCallOutcomeMutation,
  useUpdateObjectivesMutation,
} from '@/services/api/treatmentCheckInV2Api';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Notepad } from './Notepad';
import { ObjectiveRail } from './ObjectiveRail';
import { ReviewSubmitTab } from './ReviewSubmitTab';
import { TranscriptPanel } from './TranscriptPanel';
import type { AIExtractionResult } from './types';

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  DRAFT: 'outline',
  IN_CALL: 'default',
  NOTES_TAKEN: 'secondary',
  GENERATING: 'secondary',
  IN_REVIEW: 'default',
  FINALIZED: 'secondary',
  NO_ANSWER: 'outline',
  LEFT_VOICEMAIL: 'outline',
  LEFT_TEXT: 'outline',
};

function statusLabel(status: string): string {
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const TERMINAL_STATUSES: ReadonlySet<TreatmentCheckInV2Status> = new Set([
  'NO_ANSWER',
  'LEFT_VOICEMAIL',
  'LEFT_TEXT',
]);

type TabValue = 'notes' | 'review';

// ── Sub-components ───────────────────────────────────────────────────────────

const TranscriptCopyButton = ({
  checkInId,
  matterId,
  audioConversationId,
}: {
  checkInId: string;
  matterId: string;
  audioConversationId?: string | null;
}) => {
  const { data: segments } = useGetCheckInTranscriptSegmentsQuery(
    { matterId, checkInId },
    { skip: !audioConversationId },
  );
  const [copied, setCopied] = useState(false);

  if (!segments || segments.length === 0) return null;

  const handleCopy = async () => {
    const text = segments
      .map((s) => `${s.speaker === 1 ? 'Provider' : 'Operator'}: ${s.text}`)
      .join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleCopy}>
      {copied ? (
        <Check className="size-3.5 text-green-600" />
      ) : (
        <Copy className="size-3.5 text-muted-foreground" />
      )}
    </Button>
  );
};

const TerminalOutcomeSummary = ({
  status,
  callOutcomeNotes,
}: {
  status: TreatmentCheckInV2Status;
  callOutcomeNotes: string;
}) => {
  const iconMap: Record<string, React.ReactNode> = {
    LEFT_VOICEMAIL: <Voicemail className="size-8 text-muted-foreground" />,
    LEFT_TEXT: <MessageSquare className="size-8 text-muted-foreground" />,
  };
  const icon = iconMap[status] ?? (
    <PhoneOff className="size-8 text-muted-foreground" />
  );

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
      {icon}
      <h2 className="text-lg font-semibold">{statusLabel(status)}</h2>
      <p className="max-w-md text-center text-sm font-medium text-muted-foreground">
        This check-in was closed with outcome:{' '}
        <span className="font-medium">{statusLabel(status)}</span>.
      </p>
      {callOutcomeNotes && (
        <p className="max-w-md text-center text-sm font-medium text-muted-foreground">
          {callOutcomeNotes}
        </p>
      )}
    </div>
  );
}

const CallOutcomeButtons = ({
  matterId,
  checkInId,
  disabled,
  onFillForm,
  isGenerating,
}: {
  matterId: string;
  checkInId: string;
  disabled: boolean;
  onFillForm: () => void;
  isGenerating: boolean;
}) => {
  const [recordOutcome, { isLoading }] = useRecordCallOutcomeMutation();

  const handleOutcome = (outcome: CallOutcome) => {
    recordOutcome({ matterId, checkInId, outcome });
  };

  const outcomeBtnDisabled = disabled || isLoading || isGenerating;

  return (
    <div className="flex shrink-0 items-center gap-2 border-t px-4 py-2.5">
      <span className="mr-2 text-sm font-medium text-muted-foreground">
        Call outcome:
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={outcomeBtnDisabled}
        onClick={() => handleOutcome('NO_ANSWER')}
      >
        No Answer
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={outcomeBtnDisabled}
        onClick={() => handleOutcome('LEFT_VOICEMAIL')}
      >
        VM
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={outcomeBtnDisabled}
        onClick={() => handleOutcome('LEFT_TEXT')}
      >
        Left Text
      </Button>
      <div className="flex-1" />
      <Button
        size="sm"
        disabled={isGenerating}
        onClick={onFillForm}
      >
        {isGenerating ? (
          <Loader2 className="mr-1 size-3 animate-spin" />
        ) : null}
        {isGenerating ? 'Generating...' : 'Generate Notes'}
      </Button>
    </div>
  );
}

const CallBar = ({
  matterId,
  checkInId,
  initialPhoneNumber,
  checkInStatus,
  isTranscriptStreaming,
  isCallEnded,
}: {
  matterId: string;
  checkInId: string;
  initialPhoneNumber: string | null;
  checkInStatus: TreatmentCheckInV2Status | undefined;
  isTranscriptStreaming: boolean;
  isCallEnded: boolean;
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [initiateCall, { isLoading }] = useInitiateRCCallMutation();
  const [recordOutcome] = useRecordCallOutcomeMutation();
  const [callInitiated, setCallInitiated] = useState(false);
  // Show manual "Mark Connected" fallback after 5s of dialing with no auto-signal
  const [showManualConnect, setShowManualConnect] = useState(false);
  const manualConnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasEditedPhoneRef = useRef(false);

  // Clean up the manual-connect timer on unmount
  useEffect(() => {
    return () => {
      if (manualConnectTimerRef.current) {
        clearTimeout(manualConnectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    hasEditedPhoneRef.current = false;
    setPhoneNumber('');
    setCallInitiated(false);
    setShowManualConnect(false);
    if (manualConnectTimerRef.current) {
      clearTimeout(manualConnectTimerRef.current);
      manualConnectTimerRef.current = null;
    }
  }, [checkInId]);

  useEffect(() => {
    if (hasEditedPhoneRef.current || phoneNumber.trim()) return;
    const trimmedInitialPhoneNumber = initialPhoneNumber?.trim();
    if (trimmedInitialPhoneNumber) {
      setPhoneNumber(trimmedInitialPhoneNumber);
    }
  }, [initialPhoneNumber, phoneNumber]);

  const handleCall = async () => {
    if (!phoneNumber.trim()) return;
    try {
      const result = await initiateCall({
        matterId,
        to_number: phoneNumber.trim(),
        check_in_v2_id: checkInId,
      }).unwrap();

      if (result.call_uri) {
        window.location.href = result.call_uri;
        setCallInitiated(true);
        // Show manual fallback after 5 seconds if auto-detection hasn't kicked in
        manualConnectTimerRef.current = setTimeout(() => setShowManualConnect(true), 5000);
      }
    } catch {
      // Error handled by RTK Query
    }
  };

  const handleManualConnect = async () => {
    try {
      await recordOutcome({ matterId, checkInId, outcome: 'CONNECTED' }).unwrap();
    } catch {
      // Error handled by RTK Query
    }
  };

  // Derive call state from signals
  const callConnected = isTranscriptStreaming || checkInStatus === 'IN_CALL' || checkInStatus === 'NOTES_TAKEN';
  const callDone = isCallEnded || checkInStatus === 'NOTES_TAKEN';
  const isDialing = callInitiated && !callConnected && !callDone;

  return (
    <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-3">
      <Phone className="h-4 w-4 text-muted-foreground" />
      <Input
        placeholder="Enter phone number"
        value={phoneNumber}
        onChange={(e) => {
          hasEditedPhoneRef.current = true;
          setPhoneNumber(e.target.value);
        }}
        onKeyDown={(e) => e.key === 'Enter' && handleCall()}
        className="h-8 max-w-xs"
        disabled={callInitiated}
      />
      <Button
        size="sm"
        onClick={handleCall}
        disabled={isLoading || !phoneNumber.trim() || callInitiated}
      >
        {isLoading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Phone className="mr-1 h-3 w-3" />}
        Call
      </Button>
      {isDialing && !showManualConnect && (
        <Badge variant="default" className="animate-pulse">Dialing...</Badge>
      )}
      {isDialing && showManualConnect && (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
            <AlertCircle className="h-3 w-3 shrink-0" />
            Live transcript unavailable
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleManualConnect}
          >
            Mark as Connected
          </Button>
        </div>
      )}
      {callConnected && !callDone && (
        <Badge variant="destructive" className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-white" />
          In Call
        </Badge>
      )}
      {callDone && (
        <Badge variant="secondary">Call Complete</Badge>
      )}
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

export const CheckInV2View = () => {
  const { matter_id: matterId, checkInId } = useParams<{
    matter_id: string;
    checkInId: string;
  }>();

  const lastLoadedCheckInRef = useRef<TreatmentCheckInV2 | null>(null);

  const { data: checkIn, isLoading } = useGetCheckInV2Query(
    { matterId: matterId ?? '', checkInId: checkInId ?? '' },
    { skip: !matterId || !checkInId },
  );
  const { data: matter } = useGetMatterQuery(matterId ?? '', {
    skip: !matterId,
  });

  useEffect(() => {
    lastLoadedCheckInRef.current = null;
  }, [checkInId]);

  useEffect(() => {
    if (checkIn) {
      lastLoadedCheckInRef.current = checkIn;
    }
  }, [checkIn]);

  const stableCheckIn =
    checkIn ??
    (lastLoadedCheckInRef.current?.id === checkInId
      ? lastLoadedCheckInRef.current
      : null);

  const [generateNotes, { isLoading: isGenerating }] =
    useGenerateEnhancedNotesMutation();
  const [finalize, { isLoading: isFinalizing }] =
    useFinalizeCheckInV2Mutation();

  const [activeTab, setActiveTab] = useState<TabValue>('notes');
  const [fillFormError, setFillFormError] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [staleKeys, setStaleKeys] = useState<string[]>([]);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [rejectedAiSuggestedDoneIds, setRejectedAiSuggestedDoneIds] = useState<string[]>([]);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptMaximized, setTranscriptMaximized] = useState(false);
  const [isTranscriptStreaming, setIsTranscriptStreaming] = useState(false);
  const [isCallEnded, setIsCallEnded] = useState(false);
  const [updateObjectives, { isLoading: isUpdatingObjectives }] =
    useUpdateObjectivesMutation();

  const isTerminal = stableCheckIn
    ? TERMINAL_STATUSES.has(stableCheckIn.status)
    : false;

  const callOutcomeButtonsEnabled =
    stableCheckIn?.status === 'NOTES_TAKEN' ||
    stableCheckIn?.status === 'IN_CALL' ||
    stableCheckIn?.status === 'DRAFT';

  const handleFillForm = useCallback(async () => {
    if (!matterId || !checkInId) return;
    setFillFormError(null);
    // Switch to review tab immediately to show loading spinner
    setActiveTab('review');
    try {
      await generateNotes({ matterId, checkInId }).unwrap();
      // On success the query cache invalidates and extraction_result populates
    } catch (err) {
      // On error, return to notes tab and display the error
      setActiveTab('notes');
      const message =
        (err as { data?: { detail?: string; error?: string } })?.data?.detail ??
        (err as { data?: { detail?: string; error?: string } })?.data?.error ??
        'Failed to generate notes. Please try again.';
      setFillFormError(message);
      // eslint-disable-next-line no-console
      console.error('Generate Notes error:', err);
    }
  }, [matterId, checkInId, generateNotes]);


  const handleRegenerate = useCallback(async () => {
    if (!matterId || !checkInId) return;
    setFillFormError(null);
    try {
      await generateNotes({ matterId, checkInId }).unwrap();
    } catch (err) {
      const message =
        (err as { data?: { detail?: string; error?: string } })?.data?.detail ??
        (err as { data?: { detail?: string; error?: string } })?.data?.error ??
        'Failed to regenerate. Please try again.';
      setFillFormError(message);
      // eslint-disable-next-line no-console
      console.error('Regenerate error:', err);
    }
  }, [matterId, checkInId, generateNotes]);

  useEffect(() => {
    setRejectedAiSuggestedDoneIds([]);
    setFinalizeError(null);
    setStaleKeys([]);
  }, [checkInId, stableCheckIn?.extraction_generation_id]);

  const handleObjectiveStatusChange = useCallback(
    async (objectiveId: string, status: ObjectiveStatus) => {
      if (!matterId || !checkInId || !stableCheckIn?.objective_state) return;
      await updateObjectives({
        matterId,
        checkInId,
        revision: stableCheckIn.objective_state.revision,
        updates: [{ objective_id: objectiveId, status }],
      });
    },
    [stableCheckIn?.objective_state, checkInId, matterId, updateObjectives],
  );

  // Auto-show transcript when streaming starts
  const handleTranscriptStreaming = useCallback(() => {
    setTranscriptOpen(true);
    setIsTranscriptStreaming(true);
  }, []);

  // Parse extraction result for the review tab
  const extractionResult = stableCheckIn?.extraction_result as AIExtractionResult | null;
  const objectivesAssessment = extractionResult?.objectives_assessment ?? [];

  // Determine what the review tab should show
  const reviewTabContent = useMemo(() => {
    if (isGenerating) {
      return 'loading' as const;
    }
    if (extractionResult && stableCheckIn?.extraction_generation_id) {
      return 'results' as const;
    }
    return 'empty' as const;
  }, [isGenerating, extractionResult, stableCheckIn?.extraction_generation_id]);

  const initialPhoneNumber = useMemo(() => {
    if (!matter?.clients?.length) return null;

    return (
      matter.clients
        .flatMap((client) => client.phone_numbers ?? [])
        .find((phone) => Boolean(phone.phone_number?.trim()))
        ?.phone_number ?? null
    );
  }, [matter?.clients]);

  const handleFinalize = useCallback(async () => {
    if (!matterId || !checkInId) return;
    if (isSubmittingReview) return;

    setStaleKeys([]);
    setFinalizeError(null);
    setIsSubmittingReview(true);

    try {
      if (stableCheckIn?.objective_state) {
        const assessmentById = new Map(
          objectivesAssessment.map((assessment) => [assessment.objective_id, assessment]),
        );

        const suggestedDoneUpdates = stableCheckIn.objective_state.objectives
          .filter((objective) => {
            const assessment = assessmentById.get(objective.id);
            if (assessment?.suggested_status !== 'done') {
              return false;
            }
            if (objective.status !== 'open') {
              return false;
            }
            return !rejectedAiSuggestedDoneIds.includes(objective.id);
          })
          .map((objective) => ({
            objective_id: objective.id,
            status: 'done' as const,
          }));

        if (suggestedDoneUpdates.length > 0) {
          await updateObjectives({
            matterId,
            checkInId,
            revision: stableCheckIn.objective_state.revision,
            updates: suggestedDoneUpdates,
          }).unwrap();
        }
      }

      await finalize({ matterId, checkInId }).unwrap();
    } catch (err: unknown) {
      const error = err as { status?: number; data?: FinalizeConflictResponse };
      if (error.status === 409 && error.data?.stale_review_keys) {
        setStaleKeys(error.data.stale_review_keys);
        setFinalizeError(
          'Some review decisions are stale. Re-review the highlighted items before submitting.',
        );
      } else {
        setFinalizeError('Failed to finalize check-in. Please try again.');
      }
    } finally {
      setIsSubmittingReview(false);
    }
  }, [
    checkInId,
    finalize,
    isSubmittingReview,
    matterId,
    objectivesAssessment,
    rejectedAiSuggestedDoneIds,
    stableCheckIn?.objective_state,
    stableCheckIn?.objective_state?.objectives,
    updateObjectives,
  ]);

  if (!matterId || !checkInId) return null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <header className="flex shrink-0 items-center gap-3 border-b px-6 py-3">
        <h1 className="text-base font-semibold">Treatment Check-In</h1>
        {isLoading && (
          <div className="h-5 w-20 animate-pulse rounded bg-muted" />
        )}
        {stableCheckIn && (
          <Badge
            variant={STATUS_VARIANT[stableCheckIn.status] ?? 'outline'}
            className="text-[10px]"
          >
            {statusLabel(stableCheckIn.status)}
          </Badge>
        )}
      </header>

      {/* Terminal outcome — no tabs */}
      {isTerminal && stableCheckIn && (
        <TerminalOutcomeSummary
          status={stableCheckIn.status}
          callOutcomeNotes={stableCheckIn.call_outcome_notes}
        />
      )}

      {/* Tab-based UI for active check-ins */}
      {!isTerminal && (
        <Tabs
          value={activeTab}
          onValueChange={(val) => setActiveTab(val as TabValue)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="mx-6 mt-3 flex items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="notes">Taking Notes</TabsTrigger>
              <TabsTrigger value="review">Review &amp; Submit</TabsTrigger>
            </TabsList>
            {activeTab === 'review' && reviewTabContent === 'results' && (
              <div className="flex items-center gap-2">
                {stableCheckIn?.status !== 'FINALIZED' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRegenerate}
                      disabled={isGenerating}
                    >
                      {isGenerating ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-1 h-3 w-3" />
                      )}
                      Regenerate
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleFinalize}
                      disabled={isSubmittingReview || isFinalizing || isUpdatingObjectives}
                    >
                      {isSubmittingReview || isFinalizing ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : null}
                      Submit Check-In
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Tab 1: Taking Notes */}
          <TabsContent
            value="notes"
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
              <ObjectiveRail
                objectiveState={stableCheckIn?.objective_state ?? null}
                callPriorities={stableCheckIn?.call_priorities ?? null}
                isUpdatingObjectives={isUpdatingObjectives}
                onStatusChange={handleObjectiveStatusChange}
                className="hidden lg:block"
              />
              {/* Right side: Call bar + Notepad + Transcript overlay */}
              <div className="relative flex min-w-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
                <CallBar
                  matterId={matterId}
                  checkInId={checkInId}
                  initialPhoneNumber={initialPhoneNumber}
                  checkInStatus={stableCheckIn?.status}
                  isTranscriptStreaming={isTranscriptStreaming}
                  isCallEnded={isCallEnded}
                />
                <ObjectiveRail
                  objectiveState={stableCheckIn?.objective_state ?? null}
                  callPriorities={stableCheckIn?.call_priorities ?? null}
                  isUpdatingObjectives={isUpdatingObjectives}
                  onStatusChange={handleObjectiveStatusChange}
                  className="lg:hidden"
                  mobile
                />
                {stableCheckIn && (
                  <Notepad
                    key={checkInId}
                    checkInId={checkInId}
                    matterId={matterId}
                    initialContent={stableCheckIn.operator_notes ?? ''}
                    className="flex-1"
                  />
                )}

                {/* Transcript toggle button */}
                {!transcriptOpen && (
                  <button
                    type="button"
                    onClick={() => setTranscriptOpen(true)}
                    className="absolute bottom-4 right-4 z-10 flex items-center gap-2 rounded-full border bg-white px-4 py-2 text-sm font-medium shadow-lg transition-colors hover:bg-muted dark:bg-zinc-900 dark:hover:bg-zinc-800"
                    data-testid="transcript-toggle"
                  >
                    <FileText className="size-4 text-muted-foreground" />
                    Show Transcript
                  </button>
                )}

                {/* Always-mounted transcript overlay — hidden when closed so WebSocket stays connected */}
                <div
                  className={cn(
                    "absolute z-20 overflow-hidden rounded-xl border bg-white shadow-xl dark:bg-zinc-900",
                    transcriptMaximized
                      ? "inset-3"
                      : "bottom-4 left-1/2 w-[520px] max-w-[calc(100%-2rem)] -translate-x-1/2",
                    !transcriptOpen && "hidden"
                  )}
                  data-testid="transcript-overlay"
                >
                  <div className="flex items-center justify-between border-b px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Transcript</span>
                      {isTranscriptStreaming && (
                        <Badge
                          variant="destructive"
                          className="flex items-center gap-1 text-[10px]"
                        >
                          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-white" />
                          Live
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5">
                      <TranscriptCopyButton checkInId={checkInId} matterId={matterId} audioConversationId={stableCheckIn?.audio_conversation_id} />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setTranscriptMaximized((prev) => !prev)}
                      >
                        {transcriptMaximized ? (
                          <Minimize2 className="size-3.5 text-muted-foreground" />
                        ) : (
                          <Maximize2 className="size-3.5 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setTranscriptOpen(false)}
                        data-testid="transcript-close"
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                  </div>
                  <TranscriptPanel
                    groupKey={checkInId}
                    matterId={matterId}
                    checkInId={checkInId}
                    audioConversationId={stableCheckIn?.audio_conversation_id}
                    embedded
                    onStreamingStart={handleTranscriptStreaming}
                    onCallEnded={() => setIsCallEnded(true)}
                  />
                </div>
              </div>
            </div>
            {/* Error banner for Generate Notes failures */}
            {fillFormError && (
              <div className="flex shrink-0 items-center gap-2 border-t bg-destructive/10 px-4 py-2 text-sm font-medium text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span className="flex-1">{fillFormError}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-destructive hover:text-destructive"
                  onClick={() => setFillFormError(null)}
                >
                  Dismiss
                </Button>
              </div>
            )}
            {/* Call outcome buttons at the bottom */}
            <CallOutcomeButtons
              matterId={matterId}
              checkInId={checkInId}
              disabled={!callOutcomeButtonsEnabled}
              onFillForm={handleFillForm}
              isGenerating={isGenerating}
            />
          </TabsContent>

          {/* Tab 2: Review & Submit */}
          <TabsContent
            value="review"
            className="flex flex-1 flex-col overflow-hidden"
          >
            {reviewTabContent === 'loading' && (
              <div className="flex flex-1 flex-col items-center justify-center gap-4">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
                <p className="text-sm font-medium text-muted-foreground">
                  Analyzing your notes and transcript...
                </p>
              </div>
            )}

            {reviewTabContent === 'results' && extractionResult && stableCheckIn?.extraction_generation_id && (
              <div className="flex flex-1 flex-col overflow-hidden">
                <ReviewSubmitTab
                  matterId={matterId}
                  checkInId={checkInId}
                  extractionResult={extractionResult}
                  generationId={stableCheckIn.extraction_generation_id}
                  operatorNotes={stableCheckIn.operator_notes}
                  acceptedChanges={stableCheckIn.accepted_changes?.items ?? null}
                  status={stableCheckIn.status}
                  objectiveState={stableCheckIn.objective_state}
                  nextCallObjectives={stableCheckIn.next_call_objectives}
                  finalizeError={finalizeError}
                  staleKeys={staleKeys}
                  rejectedAiSuggestedDoneIds={rejectedAiSuggestedDoneIds}
                  setRejectedAiSuggestedDoneIds={setRejectedAiSuggestedDoneIds}
                />
              </div>
            )}

            {reviewTabContent === 'empty' && (
              <div className="flex flex-1 flex-col items-center justify-center gap-3">
                <p className="text-sm font-medium text-muted-foreground">
                  No results yet — click Generate Notes on the Taking Notes tab
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};
